import React, { useEffect, useState, useRef, useCallback } from 'react';
import Background from './Background';
import './About.css';
// Footer component
import Footer from './Footer';

const statData = [
  { end: 17, suffix: '+', label: 'Years Experience' },
  { end: 2016, suffix: '', label: 'GoPT Founded' },
  { end: 5000, suffix: '+', label: 'Patients Treated' },
  { end: 50, suffix: '+', label: 'Conditions Treated' },
];

const About = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [statValues, setStatValues] = useState(statData.map(() => 0));
  const statsHasAnimated = useRef(false);
  const clinicSectionRef = useRef(null);
  const founderSectionRef = useRef(null);
  const statsRef = useRef(null);

  const animateStats = useCallback(() => {
    if (statsHasAnimated.current) return;
    statsHasAnimated.current = true;

    const duration = 2000;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic for a nice deceleration
      const eased = 1 - Math.pow(1 - progress, 3);

      setStatValues(statData.map(s => Math.round(eased * s.end)));

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Intersection Observer for scroll-triggered animations
    const observerOptions = {
      threshold: 0.15,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('about-section-visible');
        }
      });
    }, observerOptions);

    // Observer for stats count-up
    const statsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateStats();
          statsObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    const clinicSection = clinicSectionRef.current;
    const founderSection = founderSectionRef.current;
    const statsSection = statsRef.current;

    if (clinicSection) {
      observer.observe(clinicSection);
    }
    if (founderSection) {
      observer.observe(founderSection);
    }
    if (statsSection) {
      statsObserver.observe(statsSection);
    }

    return () => {
      if (clinicSection) {
        observer.unobserve(clinicSection);
      }
      if (founderSection) {
        observer.unobserve(founderSection);
      }
      if (statsSection) {
        statsObserver.unobserve(statsSection);
      }
    };
  }, [animateStats]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      {/* Fixed Background - stays in place */}
      <Background />

      {/* Main About Content */}
      <div className="about-main-wrapper">

        {/* Clinic Story Section - Magazine Layout with Image */}
        {/* Now the first section on the page */}
        <section className="about-clinic-section" ref={clinicSectionRef}>
          <div className="about-clinic-container">
            {/* Left: Image */}
            <div className="about-clinic-image-wrapper">
              <div className="about-image-frame">
                <img
                  src="/clinic.jpg"
                  alt="GoPT Clinic Interior"
                  className="about-clinic-image"
                  loading="eager"
                  fetchpriority="high"
                />
              </div>
            </div>

            {/* Right: Content */}
            <div className="about-clinic-content">
              <span className="about-section-label">Our Clinic</span>
              <h2 className="about-clinic-heading">
                Where Science<br />Meets Recovery
              </h2>

              {/* Expandable Content Area */}
              <div className={`about-clinic-text-wrapper ${isExpanded ? 'about-expanded' : ''}`}>
                <div className="about-clinic-text">
                  <p>
                    Established in 2016, GoPT is a premium clinical physiotherapy centre helping people recover, move better, and live pain-free. We treat a wide range of conditions including joint and muscle pain, nerve-related issues, sports injuries, heart and lung conditions, fitness rehabilitation, and elderly care.
                  </p>
                  <p>
                    By combining expert hands-on treatment, guided exercise therapy, and advanced physiotherapy technologies under one roof, we help patients recover faster with safe and long-lasting results.
                  </p>
                  <p>
                    With centres across South Mumbai, as well as online and home-visit services, GoPT delivers high-quality care wherever it's needed.
                  </p>

                  {/* Extended Content - Shown when expanded */}
                  <div className="about-extended-story">
                    <h3 className="about-extended-title">About Us</h3>
                    <p>
                      Established in 2016, GoPT is a top-tier clinical physiotherapy centre delivering comprehensive, evidence-based rehabilitation across all age groups.
                    </p>
                    <p>
                      We cater to a wide spectrum of conditions including orthopaedic, neurological, cardiovascular, respiratory, sports-related injuries, fitness-oriented rehabilitation, and preventive care. A significant portion of our practice is dedicated to geriatric rehabilitation, where clinical precision, safety, and continuity of care are paramount.
                    </p>
                    <p>
                      At GoPT, rehabilitation is rooted in strong clinical fundamentals. Our expertise lies in hands-on manual therapy techniques, joint and soft-tissue mobilisation, and structured, evidence-based exercise programs tailored to individual needs. These are seamlessly integrated with the latest global physiotherapy technologies and conventional modalities, all available under one roof.
                    </p>
                    <p>
                      This integration allows us to enhance and accelerate the recovery process. While traditional physiotherapy is often associated with prolonged timelines, we are redefining this perception, by combining clinical skill, movement science, and technology-assisted therapy to deliver faster, more efficient, and long-lasting outcomes, without compromising safety or quality.
                    </p>
                    <p>
                      With centres located across South Mumbai's premium neighbourhoods, along with online consultations and a dedicated home-visit division, GoPT ensures consistent, high-quality care across all settings.
                    </p>
                    <p className="about-emphasis-text">
                      At GoPT, physiotherapy is not just treatment, it is precision-led rehabilitation, delivered at the highest standard.
                    </p>
                    <p className="about-tagline-text">
                      Move Better. Live Better.
                    </p>
                  </div>
                </div>
              </div>

              <button
                className="about-expand-btn"
                onClick={toggleExpand}
              >
                {isExpanded ? 'Show Less' : 'Read Entire Story'}
                <span className="about-btn-arrow">{isExpanded ? '↑' : '→'}</span>
              </button>
            </div>
          </div>
        </section>

        {/* Founder Section - Dr. Samip Gupta */}
        <section className="about-founder-section" ref={founderSectionRef}>
          <div className="about-founder-container">
            {/* Left: Content */}
            <div className="about-founder-content">
              <span className="about-section-label">Founder & Director</span>
              <h2 className="about-founder-heading">
                Dr. Samip Gupta
              </h2>

              <div className="about-founder-bio">
                <p>
                  Dr. Samip Gupta holds a Bachelor's Degree in physiotherapy, which he obtained from Rajiv Gandhi University of Health Sciences, Karnataka in the year 2008. After the successful completion of his undergraduate studies, he further enhanced his expertise by pursuing a fellowship in Orthopedic and Sports Sciences Rehabilitation at Apollo Hospital in Hyderabad.
                </p>
                <p>
                  He is currently serving as the consultant physiotherapist with Force One (Maharashtra Police). He had the important role of Head Physiotherapist at Cumballa Hill Hospital in 2009-2016. His highly specialised skills in chiropractic adjustments for correction of dearranged spines are just another feather in his cap.
                </p>
                <p>
                  During the course of his career, Dr. Gupta gained a wealth of experience, particularly in treating diverse orthopedic and sports injuries. Driven by his passion for sports, he integrated his skills into the role of a physical fitness and sports conditioning coach where he was a sports therapist for the 2nd division of I League Kenkre Football Team.
                </p>
              </div>

              {/* Stats */}
              <div className="about-founder-stats" ref={statsRef}>
                {statData.map((stat, i) => (
                  <div className="about-stat-item" key={stat.label}>
                    <span className="about-stat-number">
                      {statValues[i].toLocaleString()}{stat.suffix}
                    </span>
                    <span className="about-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Portrait */}
            <div className="about-founder-image-wrapper">
              <div className="about-image-frame">
                <img
                  src="/samip.jpg"
                  alt="Dr. Samip Gupta"
                  className="about-founder-image"
                  loading="lazy"
                />
                <div className="about-image-caption">
                  <span className="about-caption-role">Founder & Clinical Director</span>
                  <span className="about-caption-name">Dr. Samip Gupta</span>
                  <span className="about-caption-credential">BPT, Fellowship in Ortho & Sports Sciences</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Closing Statement */}
        <section className="about-closing-section">
          <div className="about-closing-container">
            <p className="about-closing-quote">
              "Physiotherapy is not merely treatment.<br />
              It is <em>precision-led rehabilitation</em>,<br />
              delivered at the highest standard."
            </p>
            <div className="about-closing-divider"></div>
            <p className="about-closing-location">
              South Mumbai • Online Consultations • Home Visits
            </p>
          </div>
        </section>

        {/* =========================================
            ADDED FOOTER (Same as Home.js)
           ========================================= */}
        <Footer />

      </div>
    </>
  );
};

export default About;